import { useSyncExternalStore } from "react";

export interface PersistedStoreOptions<T extends string> {
    storageKey: string;
    getFallback: () => T;
    isValid: (value: string | null) => value is T;
    apply: (value: T) => void;
}

export interface PersistedStore<T extends string> {
    set: (value: T, options?: { persist?: boolean }) => void;
    clear: () => void;
    useValue: () => T;
    useStoredValue: () => T | null;
    useSetValue: () => (value: T) => void;
    refresh: () => void;
}

export function createPersistedStore<T extends string>({ storageKey, getFallback, isValid, apply }: PersistedStoreOptions<T>): PersistedStore<T> {
    function readStored(): T | null {
        if (typeof localStorage === "undefined") {
            return null;
        }

        const value = localStorage.getItem(storageKey);

        return isValid(value) ? value : null;
    }

    function resolve(storedValue: T | null): T {
        return storedValue ?? getFallback();
    }

    let stored: T | null = readStored();
    let current: T = resolve(stored);
    const listeners = new Set<() => void>();

    function emit() {
        for (const listener of listeners) {
            listener();
        }
    }

    function sync(nextStored: T | null) {
        const nextCurrent = resolve(nextStored);
        const storedChanged = nextStored !== stored;
        const currentChanged = nextCurrent !== current;

        if (!storedChanged && !currentChanged) {
            return;
        }

        stored = nextStored;

        if (currentChanged) {
            current = nextCurrent;

            if (typeof document !== "undefined") {
                apply(current);
            }
        }

        emit();
    }

    if (typeof document !== "undefined") {
        apply(current);
    }

    if (typeof window !== "undefined") {
        window.addEventListener("storage", event => {
            if (event.key !== storageKey) {
                return;
            }

            sync(isValid(event.newValue) ? event.newValue : null);
        });
    }

    function set(value: T, options: { persist?: boolean } = {}) {
        const { persist = true } = options;

        if (!persist) {
            if (value !== current) {
                current = value;

                if (typeof document !== "undefined") {
                    apply(current);
                }

                emit();
            }

            return;
        }

        if (typeof localStorage !== "undefined") {
            localStorage.setItem(storageKey, value);
        }

        sync(value);
    }

    function clear() {
        if (typeof localStorage !== "undefined") {
            localStorage.removeItem(storageKey);
        }

        sync(null);
    }

    function subscribe(listener: () => void) {
        listeners.add(listener);

        return () => {
            listeners.delete(listener);
        };
    }

    function useValue(): T {
        return useSyncExternalStore(subscribe, () => current, getFallback);
    }

    function useStoredValue(): T | null {
        return useSyncExternalStore(
            subscribe,
            () => stored,
            () => null
        );
    }

    function useSetValue(): (value: T) => void {
        return set;
    }

    return { set, clear, useValue, useStoredValue, useSetValue, refresh: () => sync(readStored()) };
}
